import { useQuery } from "@tanstack/react-query";

import { useApiClient } from "../api/louise-api";
import { medicationLogKeys } from "./hooks/useMedicationLogs";
import type { MedicationLogRow } from "./medication-log-fetch";
import { useTimezone } from "../preferences";
import {
  getTodayString,
  parseLocalDate,
  toLocalDateString,
} from "../utils/date";

/**
 * Whether a dose has already been given from this calendar line's vial.
 *
 * ### Why this is a hook in `care` and not code in the coach
 *
 * The same reason `useMedicationFormCorrection` is. Nothing in the
 * injection-coach package may reach the API except the consent receipt;
 * `no-retention.test.ts` asserts it module by module, and the reason is that
 * the package holds a camera. So `care` reads the fact and the surface that
 * knows which record it is passes it down as a plain value.
 *
 * ### Why the server does the filtering
 *
 * Anaïs, 2026-09-28: it must be **the same calendar line** that answers, not
 * another line carrying the same medication. Every other read in this app
 * fetches the whole patient's logs for a date range and filters client-side
 * with `sameMedication`, which falls back to matching on the medication *name*
 * when the id is missing — exactly the behaviour that would let a second
 * Menopur line say this vial is open.
 *
 * The API already supports the narrow read: *"Return only logs for this patient
 * medication. Valid only with a bounded date range."* Nothing in the app used
 * it until now. The id is the only key here, so a name can never answer.
 *
 * ### Why 28 days
 *
 * It is how long a reconstituted Menopur vial is medicine. A dose logged inside
 * that window means the vial she is holding was opened; one logged before it
 * belongs to a vial that has since expired, and she needs the mixing walk
 * again.
 *
 * ### Why `null` is a real answer
 *
 * `null` means the record could not be read — not that there is no dose. The
 * two must not collapse: skipping the mixing of an unmixed vial means injecting
 * powder, so only a positive answer skips it. See `presentationDecision`.
 */

/**
 * Whether this medication is one where the answer can change anything.
 *
 * Exactly one is, today. Called at the surfaces rather than inside the hook so
 * the read never starts for an oral tablet or for Gonal-F — it used to run once
 * per medication row on screen, N requests for an answer one of them could use.
 */
export function vialEvidenceMatters(
  protocols: readonly { presentation?: string }[],
): boolean {
  return protocols.some((protocol) => Boolean(protocol.presentation));
}

/** A reconstituted vial's life, and therefore the window worth reading. */
export const OPENED_VIAL_WINDOW_DAYS = 28;

/**
 * A calendar date N days back, in **her** timezone.
 *
 * The endpoint reads `startDate` and `endDate` as patient-local dates and is
 * sent her timezone alongside them, so building them from `toISOString()` —
 * which is UTC — asks for a different window than the one it is told to use.
 * In Europe/Paris at 00:30 on the 30th, UTC is still the 29th: the request
 * excluded the dose she logged twenty minutes earlier, `hasEarlierDose` came
 * back `false`, and she was walked through mixing a vial she mixed that night.
 *
 * `parseLocalDate` reads the string back as local midnight and only whole days
 * are shifted from there, so nothing crosses a zone boundary on the way out.
 */
function isoDaysAgo(days: number, timezone: string): string {
  const day = parseLocalDate(getTodayString(timezone));
  day.setDate(day.getDate() - days);
  return toLocalDateString(day);
}

export function useOpenedVialEvidence(
  patientMedicationId: string | null | undefined,
  options: { enabled?: boolean } = {},
): boolean | null {
  const { client, tenantId, patientId } = useApiClient();
  const timezone = useTimezone();
  const enabled = (options.enabled ?? true) && Boolean(patientMedicationId);
  const startDate = isoDaysAgo(OPENED_VIAL_WINDOW_DAYS - 1, timezone);
  const endDate = isoDaysAgo(0, timezone);

  const query = useQuery({
    // Under the medication-log keys, where `useLogMedicationDose`'s
    // invalidation reaches it. Its own namespace meant it was never reached at
    // all, and the answer would have stayed stale until a reload.
    //
    // In its own segment, because reached is not the same as refetched. The log
    // list beside it is invalidated with `refetchType: "none"` on purpose, and
    // that would have left this one holding "no dose" for the five minutes of
    // `staleTime` below — long enough for her to finish a walk, reopen the
    // coach from the same screen, and be offered the mixing steps for the vial
    // she had just mixed. The mutation now refetches this key by name.
    queryKey: [
      ...medicationLogKeys.openedVial(tenantId ?? "", patientId ?? ""),
      patientMedicationId ?? "",
      startDate,
      endDate,
      timezone,
    ],
    // The same window `useMedicationLogs` uses. Without it, every medication
    // row refetched on each tab focus for an answer that changes once a day.
    staleTime: 1000 * 60 * 5,
    enabled: enabled && Boolean(tenantId && patientId),
    queryFn: async () => {
      const response = await client.GET(
        "/patients/{patientId}/medications/logs",
        {
          params: {
            path: { patientId: patientId ?? "" },
            query: {
              startDate,
              endDate,
              timezone,
              // The whole point of this hook. Server-side, so no name match can
              // creep in and answer for another calendar line.
              patientMedicationId: patientMedicationId ?? undefined,
            },
          },
        },
      );
      if (response.error) throw response.error;
      const rows = response.data?.data ?? [];
      // A skipped or missed dose did not open a vial. Only one she gave did.
      return rows.some(
        (row: MedicationLogRow) =>
          row.status === "taken" || row.status === "late",
      );
    },
  });

  if (!enabled) return null;
  // Loading and failure are both "we do not know", and they must stay that way:
  // returning `false` while the read is in flight would walk her through mixing
  // a vial she opened last night.
  return query.data ?? null;
}
